import { Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import { TransactionRecord } from '../../service/analytics';
import { getItemByIDs, ItemDef } from '../../service/inventory';
import { getPublicProfiles, UserProfile } from '../../service/social';
import { CurrencyIcon } from '../storeComponents/CurrencyIcon';

const PLATFORM_TARGET = 'streamsix';

export interface TransactionListItemProps {
    datas?: TransactionRecord[];
    index: number;
    date: string;
}

export interface TransactionListItemPropsDisplayData {
    totalHours: string;
}

export const TransactionListItem: React.FC<TransactionListItemProps> = ({ datas, index, date }) => {
    const intl = useIntl();

    const [profileMap, setProfileMap] = usePageDataCache<{ [id: string]: UserProfile }>(
        'transactionProfileMap',
        {},
    );
    const [costItems, setCostItems] = usePageDataCache<ItemDef[]>('costItems', []);

    const getExpiryDate = (data: TransactionRecord) => {
        date = '';
        if (data.store === 'Xsolla') {
            if (data.xsolla_subscription) {
                date = intl.formatDate(
                    data.xsolla_subscription.date_next_charge || data.xsolla_subscription.date_end,
                );
            }
        } else if (data.store === 'GooglePlay') {
            if (data.playstore_subscription) {
                date = intl.formatDate(parseInt(data.playstore_subscription.expiryTimeMillis));
            }
        } else if (data.store === 'AppleAppStore') {
            if (data.appstore_subscription) {
                date = intl.formatDate(data.appstore_subscription.expiresDate);
            }
        } else if (data.store === 'Stripe') {
            if (data.stripe_subscription) {
                date = intl.formatDate(data.stripe_subscription.current_period_end * 1000);
            }
        }

        return (
            date && (
                <Text className="expiry-text">
                    <FormattedMessage
                        id="transaction.subscription.expire_on"
                        values={{
                            date: date,
                        }}
                    />
                </Text>
            )
        );
    };

    const getSubscribeTarget = (data: TransactionRecord) => {
        let target = null;
        if (data.store === 'Stripe') {
            if (data.stripe_subscription) {
                const id = data.stripe_subscription.metadata['streamsix_subscribe_target_id'];
                target = profileMap[id];
            }
        }
        return (
            target && (
                <Text className="expiry-text">
                    <FormattedMessage
                        id="transaction.subscription.to"
                        values={{
                            username: target?.username,
                        }}
                    />
                </Text>
            )
        );
    };

    const getSubscribeSource = (data: TransactionRecord) => {
        let source = null;
        if (data.store === 'Stripe') {
            if (data.stripe_subscription) {
                const id = data.stripe_subscription.metadata['streamsix_user_id'];
                source = profileMap[id];
            }
        }
        return (
            source && (
                <Text className="expiry-text">
                    <FormattedMessage
                        id="transaction.subscription.from"
                        values={{
                            username: source?.username,
                        }}
                    />
                </Text>
            )
        );
    };

    const getResumeDate = (data: TransactionRecord) => {
        date = '';
        if (data.store === 'GooglePlay') {
            if (data.playstore_subscription) {
                date = intl.formatDate(parseInt(data.playstore_subscription.autoResumeTimeMillis));
            }
        }

        return (
            date && (
                <Text className="expiry-text">
                    <FormattedMessage
                        id="transaction.subscription.resume_on"
                        values={{
                            date: date,
                        }}
                    />
                </Text>
            )
        );
    };

    const getEndDate = (data: TransactionRecord) => {
        date = '';
        if (data.store === 'Stripe') {
            if (data.stripe_subscription) {
                date = intl.formatDate(data.stripe_subscription.current_period_end * 1000);
            }
        }

        return (
            date && (
                <Text className="expiry-text">
                    <FormattedMessage
                        id="transaction.subscription.expire_on"
                        values={{
                            date: date,
                        }}
                    />
                </Text>
            )
        );
    };

    const purchaseRecord = (data: TransactionRecord, msgID: string) => (
        <>
            <Flex className={'compact-flex-container1'}>
                <Text className="purchase-header">
                    <FormattedMessage id={msgID} defaultMessage={'Purchase'} />
                </Text>
                <Text className="store-text">
                    <i>{data.store}</i>
                </Text>
            </Flex>
            <Flex className={'compact-flex-container2'}>
                {getSubscribeTarget(data)}
                {data.cost_currency ? (
                    <Text className="cost-text">
                        {intl.formatNumber(data.cost_quantity, {
                            style: 'currency',
                            currency: data.cost_currency,
                            currencyDisplay: 'code',
                        })}
                    </Text>
                ) : (
                    <div></div>
                )}
                {getExpiryDate(data)}
            </Flex>
            <Flex className={'compact-flex-container3'}>
                <Flex className="purchase-text">
                    {data.products.map((product, idx) => {
                        return (
                            <Flex key={idx} className={'purchase-align'}>
                                {product.items[0].item_def && (
                                    <CurrencyIcon itemID={product.items[0].item_def.item_id} />
                                )}
                                <Text>{product.product_name}</Text>
                            </Flex>
                        );
                    })}
                </Flex>
            </Flex>
        </>
    );

    const playStoreRecord = (data: TransactionRecord) => {
        switch (data.notification_type) {
            case '2': // renew
                return purchaseRecord(data, 'transaction.renewal');
            case '1': // recover
            case '3': // cancel
            case '7': // restarted
            case '10': // pause
            case '11': // schedule change
            case '13': // expire
                let textID = 'transaction.subscription.recover';
                let dateMsg = getExpiryDate(data);
                if (data.notification_type === '3') {
                    textID = 'transaction.subscription.cancel';
                    dateMsg = getExpiryDate(data);
                } else if (data.notification_type === '7') {
                    textID = 'transaction.subscription.resumed';
                    dateMsg = getResumeDate(data);
                } else if (data.notification_type === '10') {
                    textID = 'transaction.subscription.pause';
                    dateMsg = getResumeDate(data);
                } else if (data.notification_type === '11') {
                    textID = 'transaction.subscription.schedule_change';
                    dateMsg = getResumeDate(data);
                } else if (data.notification_type === '13') {
                    textID = 'transaction.subscription.expire';
                    dateMsg = getExpiryDate(data);
                }

                return (
                    <>
                        <Flex className={'compact-flex-container1'}>
                            <Text className="purchase-header">
                                <FormattedMessage id={textID} />
                            </Text>
                            <Text className="store-text">
                                <i>{data.store}</i>
                            </Text>
                        </Flex>
                        <Flex className={'compact-flex-container2'}>{dateMsg}</Flex>
                        <Flex className={'compact-flex-container3'}>
                            <Flex className={'purchase-text'}>
                                {data.products.map((product, idx) => {
                                    return <Text key={idx}>{product.product_name}</Text>;
                                })}
                            </Flex>
                        </Flex>
                    </>
                );
        }
    };

    const appStoreRecord = (data: TransactionRecord) => {
        switch (data.notification_type) {
            case 'DID_RENEW':
                return purchaseRecord(data, 'transaction.renewal');
            case 'SUBSCRIBED':
            case 'REVOKE':
            case 'RENEWAL_EXTENDED':
            case 'REFUND_DECLINED':
            case 'REFUND':
            case 'PRICE_INCREASE':
            case 'OFFER_REDEEMED':
            case 'GRACE_PERIOD_EXPIRED':
            case 'EXPIRED':
            case 'DID_FAIL_TO_RENEW':
            case 'DID_CHANGE_RENEWAL_STATUS':
            case 'DID_CHANGE_RENEWAL_PREF':
            case 'CONSUMPTION_REQUEST':
                let textID = 'transaction.subscription.subscribe';
                let dateMsg = getExpiryDate(data);
                if (data.notification_type === 'REVOKE') {
                    textID = 'transaction.subscription.revoke';
                } else if (data.notification_type === 'RENEWAL_EXTENDED') {
                    textID = 'transaction.subscription.renewal_extended';
                } else if (data.notification_type === 'REFUND_DECLINED') {
                    textID = 'transaction.subscription.refund_declined';
                } else if (data.notification_type === 'REFUND') {
                    textID = 'transaction.subscription.refund';
                } else if (data.notification_type === 'PRICE_INCREASE') {
                    textID = 'transaction.subscription.price_increase';
                } else if (data.notification_type === 'OFFER_REDEEMED') {
                    textID = 'transaction.subscription.offer_redeemed';
                } else if (data.notification_type === 'GRACE_PERIOD_EXPIRED') {
                    textID = 'transaction.subscription.grace_period_expired';
                } else if (data.notification_type === 'EXPIRED') {
                    textID = 'transaction.subscription.expire';
                } else if (data.notification_type === 'DID_FAIL_TO_RENEW') {
                    textID = 'transaction.subscription.fail_to_renew';
                } else if (data.notification_type === 'DID_CHANGE_RENEWAL_STATUS') {
                    if (data.sub_type === 'AUTO_RENEW_ENABLED') {
                        textID = 'transaction.subscription.auto_renew_enable';
                    } else if (data.sub_type === 'AUTO_RENEW_DISABLED') {
                        textID = 'transaction.subscription.auto_renew_disable';
                    }
                } else if (data.notification_type === 'DID_CHANGE_RENEWAL_PREF') {
                    if (data.sub_type === 'UPGRADE') {
                        textID = 'transaction.subscription.upgrade';
                    } else if (data.sub_type === 'DOWNGRADE') {
                        textID = 'transaction.subscription.downgrade';
                    } else {
                        textID = 'transaction.subscription.cancel_downgrade';
                    }
                } else if (data.notification_type === 'CONSUMPTION_REQUEST') {
                    textID = 'transaction.subscription.consumption_request';
                }

                return (
                    <>
                        <Flex className={'compact-flex-container1'}>
                            <Text className="purchase-header">
                                <FormattedMessage id={textID} />
                            </Text>
                            <Text className="store-text">
                                <i>{data.store}</i>
                            </Text>
                        </Flex>
                        <Flex className={'compact-flex-container2'}>{dateMsg}</Flex>
                        <Flex className={'compact-flex-container3'}>
                            <Flex className={'purchase-text'}>
                                {data.products.map((product, idx) => {
                                    return <Text key={idx}>{product.product_name}</Text>;
                                })}
                            </Flex>
                        </Flex>
                    </>
                );
        }
    };

    const xsollaRecord = (data: TransactionRecord) => {
        switch (data.notification_type) {
            case 'payment': // renew subscription
                return purchaseRecord(data, 'transaction.renewal');
            case 'non_renewal_subscription': // stop auto renewal
            case 'cancel_subscription': // subscription is stopped
                let textID = 'transaction.subscription.auto_renew_disable';
                let dateMsg = getExpiryDate(data);
                if (data.notification_type === 'cancel_subscription') {
                    textID = 'transaction.subscription.cancel';
                }

                return (
                    <>
                        <Flex className={'compact-flex-container1'}>
                            <Text className="purchase-header">
                                <FormattedMessage id={textID} />
                            </Text>
                            <Text className="store-text">
                                <i>{data.store}</i>
                            </Text>
                        </Flex>
                        <Flex className={'compact-flex-container2'}>{dateMsg}</Flex>
                        <Flex className={'compact-flex-container3'}>
                            <Flex className={'purchase-text'}>
                                {data.products.map((product, idx) => {
                                    return <Text key={idx}>{product.product_name}</Text>;
                                })}
                            </Flex>
                        </Flex>
                    </>
                );
        }
    };

    const stripeRecord = (data: TransactionRecord) => {
        switch (data.notification_type) {
            case 'invoice.paid': // renew subscription
                return purchaseRecord(data, 'transaction.renewal');
            case 'customer.subscription.created':
            case 'customer.subscription.updated.upgrade':
            case 'customer.subscription.updated.downgrade':
            case 'customer.subscription.updated.cancel':
            case 'customer.subscription.deleted':
            case 'customer.subscription.paused':
            case 'customer.subscription.resumed':
            case 'invoice.finalization_failed':
            case 'invoice.payment_failed':
            case 'invoice.payment_action_required':
            // for user who is the target of subscription
            case 'customer.subscription.purchase.receive':
            case 'customer.subscription.renewal.receive':
            case 'customer.subscription.updated.upgrade.receive':
            case 'customer.subscription.updated.downgrade.receive':
            case 'customer.subscription.updated.cancel.receive':
            case 'customer.subscription.deleted.receive':
                let textID = '';
                let dateMsg = undefined;
                if (data.notification_type === 'customer.subscription.created') {
                    textID = 'transaction.subscription.created';
                } else if (data.notification_type === 'customer.subscription.updated.upgrade') {
                    textID = 'transaction.subscription.updated.upgrade';
                    dateMsg = getSubscribeTarget(data);
                } else if (data.notification_type === 'customer.subscription.updated.downgrade') {
                    textID = 'transaction.subscription.updated.downgrade';
                    dateMsg = getSubscribeTarget(data);
                } else if (data.notification_type === 'customer.subscription.updated.cancel') {
                    textID = 'transaction.subscription.cancel';
                    dateMsg = (
                        <>
                            {getSubscribeTarget(data)}
                            {getEndDate(data)}
                        </>
                    );
                } else if (data.notification_type === 'customer.subscription.deleted') {
                    textID = 'transaction.subscription.expire';
                    dateMsg = getSubscribeTarget(data);
                } else if (data.notification_type === 'customer.subscription.paused') {
                    textID = 'transaction.subscription.pause';
                } else if (data.notification_type === 'customer.subscription.resumed') {
                    textID = 'transaction.subscription.resumed';
                } else if (data.notification_type === 'invoice.finalization_failed') {
                    textID = 'transaction.subscription.invoice.finalization_failed';
                } else if (data.notification_type === 'invoice.payment_failed') {
                    textID = 'transaction.subscription.invoice.payment_failed';
                } else if (data.notification_type === 'invoice.payment_action_required') {
                    textID = 'transaction.subscription.invoice.payment_action_required';
                } else if (data.notification_type === 'customer.subscription.purchase.receive') {
                    textID = 'transaction.purchase';
                    dateMsg = (
                        <>
                            {getSubscribeSource(data)}
                            {data.cost_currency ? (
                                <Text className="cost-text">
                                    {intl.formatNumber(data.cost_quantity, {
                                        style: 'currency',
                                        currency: data.cost_currency,
                                        currencyDisplay: 'code',
                                    })}
                                </Text>
                            ) : (
                                <div></div>
                            )}
                            {getExpiryDate(data)}
                        </>
                    );
                } else if (data.notification_type === 'customer.subscription.renewal.receive') {
                    textID = 'transaction.renewal';
                    dateMsg = (
                        <>
                            {getSubscribeSource(data)}
                            {data.cost_currency ? (
                                <Text className="cost-text">
                                    {intl.formatNumber(data.cost_quantity, {
                                        style: 'currency',
                                        currency: data.cost_currency,
                                        currencyDisplay: 'code',
                                    })}
                                </Text>
                            ) : (
                                <div></div>
                            )}
                            {getExpiryDate(data)}
                        </>
                    );
                } else if (
                    data.notification_type === 'customer.subscription.updated.upgrade.receive'
                ) {
                    textID = 'transaction.subscription.updated.upgrade';
                    dateMsg = getSubscribeSource(data);
                } else if (
                    data.notification_type === 'customer.subscription.updated.downgrade.receive'
                ) {
                    textID = 'transaction.subscription.updated.downgrade';
                    dateMsg = getSubscribeSource(data);
                } else if (
                    data.notification_type === 'customer.subscription.updated.cancel.receive'
                ) {
                    textID = 'transaction.subscription.cancel';
                    dateMsg = (
                        <>
                            {getSubscribeSource(data)}
                            {getEndDate(data)}
                        </>
                    );
                } else if (data.notification_type === 'customer.subscription.deleted.receive') {
                    textID = 'transaction.subscription.expire';
                    dateMsg = getSubscribeSource(data);
                }

                return (
                    <>
                        <Flex className={'compact-flex-container1'}>
                            <Text className="purchase-header">
                                <FormattedMessage id={textID} />
                            </Text>
                            <Text className="store-text">
                                <i>{data.store}</i>
                            </Text>
                        </Flex>
                        <Flex className={'compact-flex-container2'}>{dateMsg}</Flex>
                        <Flex className={'compact-flex-container3'}>
                            <Flex className={'purchase-text'}>
                                {data.products.map((product, idx) => {
                                    return (
                                        <Flex key={idx}>
                                            {product.items[0].item_def && (
                                                <CurrencyIcon
                                                    itemID={product.items[0].item_def.item_id}
                                                />
                                            )}
                                            <Text>{product.product_name}</Text>
                                        </Flex>
                                    );
                                })}
                            </Flex>
                        </Flex>
                    </>
                );
        }
    };

    const transferInRecord = (data: TransactionRecord) => {
        const sender = profileMap[data.transfer_sender];

        return (
            <>
                <Flex className={'compact-flex-container1'}>
                    <Text className="purchase-header">
                        <FormattedMessage id={'transaction.transfer_in'} />
                    </Text>
                    <Text className="store-text">
                        <i>{sender?.username}</i>
                    </Text>
                </Flex>
                <Flex className={'compact-flex-container2'}></Flex>
                <Flex className={'compact-flex-container3'}>
                    <Flex className={'purchase-text'}>
                        {data.inventory_items.map((inv, idx) => {
                            return (
                                <Text key={idx}>
                                    <FormattedMessage
                                        id={'transaction.transfer_in.item_quantity'}
                                        values={{
                                            quantity: inv.quantity,
                                            item_name: inv.inventory_item.item_name,
                                        }}
                                    />
                                </Text>
                            );
                        })}
                    </Flex>
                </Flex>
            </>
        );
    };

    const transferOutRecord = (data: TransactionRecord) => {
        const receiver = profileMap[data.transfer_receiver];

        return (
            <>
                <Flex className={'compact-flex-container1'}>
                    <Text className="purchase-header">
                        <FormattedMessage id={'transaction.transfer_out'} />
                    </Text>
                    <Text className="store-text">
                        <i>{receiver?.username}</i>
                    </Text>
                </Flex>
                <Flex className={'compact-flex-container2'}></Flex>
                <Flex className={'compact-flex-container3'}>
                    <Flex className={'purchase-text'}>
                        {data.inventory_items.map((inv, idx) => {
                            return (
                                <Text key={idx}>
                                    <FormattedMessage
                                        id={'transaction.transfer_out.item_quantity'}
                                        values={{
                                            quantity: inv.quantity,
                                            item_name: inv.inventory_item.item_name,
                                        }}
                                    />
                                </Text>
                            );
                        })}
                    </Flex>
                </Flex>
            </>
        );
    };

    const payoutRecord = (data: TransactionRecord) => (
        <>
            <Flex className={'compact-flex-container1'}>
                <Text className="purchase-header">
                    <FormattedMessage id={'todo'} defaultMessage={'Payout'} />
                </Text>
                <Text className="store-text">
                    <i>{data.store}</i>
                </Text>
            </Flex>
            <Flex className={'compact-flex-container2'}>
                {data.cost_currency ? (
                    <Text className="cost-text">
                        {intl.formatNumber(data.cost_quantity, {
                            style: 'currency',
                            currency: data.cost_currency,
                            currencyDisplay: 'code',
                        })}
                    </Text>
                ) : (
                    <div></div>
                )}
                {getExpiryDate(data)}
            </Flex>
            <Flex className={'compact-flex-container3'}>
                <Flex className="purchase-text">
                    {data.products.map((product, idx) => {
                        return (
                            <Flex key={idx}>
                                <Text>{product.product_name}</Text>
                            </Flex>
                        );
                    })}
                </Flex>
            </Flex>
        </>
    );

    const refundRecord = (data: TransactionRecord) => {
        const costItem = costItems.find((x) => x.item_id === data.cost_item_id);

        return (
            <>
                <Flex className={'compact-flex-container1'}>
                    <Text className="purchase-header">
                        <FormattedMessage id={'transaction.refund'} defaultMessage={'Refund'} />
                    </Text>
                    <Text className="store-text">
                        <i>{data.store}</i>
                    </Text>
                </Flex>
                <Flex className={'compact-flex-container2'}>
                    {data.cost_currency ? (
                        <Text className="cost-text">
                            {intl.formatNumber(data.cost_quantity, {
                                style: 'currency',
                                currency: data.cost_currency,
                                currencyDisplay: 'code',
                            })}
                        </Text>
                    ) : (
                        <div></div>
                    )}
                    {costItem ? (
                        <Text className="cost-text">
                            <FormattedMessage
                                id={'transaction.refund.cost_quantity'}
                                values={{
                                    quantity: data.cost_quantity,
                                    item_name: costItem.item_name,
                                }}
                            />
                        </Text>
                    ) : (
                        <div></div>
                    )}
                </Flex>
                <Flex className={'compact-flex-container3'}>
                    <Flex className="purchase-text">
                        {data.products.map((product, idx) => {
                            return (
                                <Flex key={idx}>
                                    <Text>{product.product_name}</Text>
                                </Flex>
                            );
                        })}
                    </Flex>
                </Flex>
            </>
        );
    };

    const getTransferUsers = async (datas: TransactionRecord[] | undefined) => {
        if (datas == undefined) {
            return;
        }

        const filtered = datas.filter(
            (x) => x.event === 'inventory.transfer_in' || x.event === 'inventory.transfer_out',
        );
        const ids1 = filtered.map((data) => data.transfer_sender);
        const ids2 = filtered.map((data) => data.transfer_receiver);

        const subFiltered = datas.filter(
            (x) => x.event === 'inventory.purchase' || x.event === 'inventory.notify_stripe',
        );
        const ids3 = subFiltered
            .map((data) =>
                data.stripe_subscription
                    ? data.stripe_subscription.metadata['streamsix_subscribe_target_id']
                    : '',
            )
            .filter((x) => x !== PLATFORM_TARGET && x !== '');
        const ids4 = subFiltered
            .map((data) =>
                data.stripe_subscription
                    ? data.stripe_subscription.metadata['streamsix_user_id']
                    : '',
            )
            .filter((x) => x !== PLATFORM_TARGET && x !== '');

        const ids = [...ids1, ...ids2, ...ids3, ...ids4];
        const profiles = await getPublicProfiles(ids);
        setProfileMap(profiles);
    };

    const getCostItems = async (datas: TransactionRecord[] | undefined) => {
        if (datas == undefined) {
            return;
        }

        const filtered = datas.filter(
            (x) => x.event === 'inventory.refund' && x.cost_item_id !== '',
        );
        const ids = filtered.map((data) => data.cost_item_id);
        if (ids.length > 0) {
            const items = await getItemByIDs(ids);
            setCostItems(items);
        }
    };

    useEffect(() => {
        getTransferUsers(datas);
        getCostItems(datas);
    }, []);

    const renderComponent = (data: TransactionRecord) => {
        switch (data.event) {
            case 'inventory.purchase':
                return purchaseRecord(data, 'transaction.purchase');
            case 'inventory.notify_playstore':
                return playStoreRecord(data);
            case 'inventory.notify_appstore':
                return appStoreRecord(data);
            case 'inventory.notify_xsolla':
                return xsollaRecord(data);
            case 'inventory.notify_stripe':
                return stripeRecord(data);
            case 'inventory.transfer_in':
                return transferInRecord(data);
            case 'inventory.transfer_out':
                return transferOutRecord(data);
            case 'inventory.payout':
                return payoutRecord(data);
            case 'inventory.refund':
                return refundRecord(data);
            default:
                return <></>;
        }
    };

    return (
        <>
            <VStack className="transaction-compact-vstack">
                <Flex className={'divider'} />
                <Flex className={`transaction-compact`}>
                    <Flex className={'compact-flex-date-container'}>
                        <Text className="header-date-text">{date}</Text>
                    </Flex>
                </Flex>
                {datas &&
                    datas.map((data, idx) => (
                        <Flex
                            key={idx}
                            className={`transaction-compact${idx % 2 === 1 ? '' : '-light'}`}
                        >
                            {renderComponent(data)}
                        </Flex>
                    ))}
            </VStack>
        </>
    );
};
